import { css } from '@emotion/react'
import { colors, font, measures } from 'styles/variables'

const { unit } = measures

export const mobileContainer = css`
  background-color: ${colors.grays.gray6} !important;
  min-height: 100vh;
  min-height: 100dvh; /* Use dynamic viewport height */
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  z-index: 1000;
  overflow-y: auto;
  
  /* Handle safe areas for mobile devices */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  
  /* Override any parent container backgrounds */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${colors.grays.gray6};
    z-index: -1;
  }
  
  /* Ensure no default background colors from browser */
  * {
    background-color: inherit;
  }
`

export const header = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${6 * unit}px ${4 * unit}px ${4 * unit}px;
  background-color: transparent;
  flex-shrink: 0;
  margin-top: ${4 * unit}px;
  transform: translateY(20px);
  
  /* Ensure all child elements inherit the transparent background */
  * {
    background-color: transparent !important;
  }
`

export const backArrow = css`
  color: #ffffff;
  font-size: 22px;
  cursor: pointer;
  user-select: none;
  position: absolute;
  left: ${4 * unit}px;
  top: ${0.7 * unit}px;
`

export const headerContent = css`
  text-align: center;
`

export const title = css`
  color: ${colors.white12};
  font-size: 22px;
  font-weight: ${font.weight.bold};
  font-style: italic;
  margin: 0;
  margin-top: -${6 * unit}px;
  margin-bottom: ${4 * unit}px;
  line-height: 1.2;
  background-color: transparent !important;
  display: block;
`

export const stepIndicator = css`
  color: ${colors.greens.green2};
  font-size: 13px;
  font-weight: ${font.weight.normal};
  margin: 0;
  background-color: transparent !important;
  display: block;
`

export const formContainer = css`
  flex: 1;
  padding: 0 ${4 * unit}px;
  padding-bottom: ${22 * unit}px; /* Add space for fixed footer */
  overflow-y: auto;
  overflow-x: hidden;
  transform: translateY(20px);
  
  /* Enable momentum scrolling on iOS */
  -webkit-overflow-scrolling: touch;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.grays.gray3};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.grays.gray2};
  }
  
  /* Hide scrollbar on mobile devices */
  @media (max-width: 768px) {
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`

export const sectionTitle = css`
  font-size: 16px;
  font-weight: ${font.weight.bold};
  color: ${colors.white12};
  margin: 0;
  margin-bottom: ${4 * unit}px;
`

export const inputContainer = css`
  position: relative;
`

export const countryInputContainer = css`
  position: relative;
  margin-bottom: ${6 * unit}px;
  z-index: 1000;
  
  /* Use transform to scale up the dropdown */
  & > div > div > div {
    transform: scaleY(0.9) !important;
    transform-origin: top !important;
    margin-bottom: ${1.5 * unit}px;
  }
  
  /* Fix z-index for dropdown menu */
  & [class*="react-select__menu"] {
    z-index: 9999 !important;
  }
  
  & .react-select__menu {
    z-index: 9999 !important;
  }
  
  /* Override placeholder styles for oldInput component */
  & .css-1wa3eu0-placeholder {
    font-size: 14px !important;
    color: ${colors.grays.gray2} !important;
  }
  
  & div[class*="placeholder"] {
    font-size: 14px !important;
    color: ${colors.grays.gray2} !important;
  }
  
  /* Target any div that contains the placeholder text */
  & div {
    &:contains("Select your country") {
      font-size: 14px !important;
      color: ${colors.grays.gray2} !important;
    }
  }
`

export const inputWrapper = css`
  position: relative;
  width: 100%;
  max-width: 100%;
  
  /* Style the FormInput component */
  & > div {
    position: relative;
    width: 100%;
    max-width: 100%;
    
    & input {
      width: 100% !important;
      max-width: 100% !important;
      background-color: transparent !important;
      border: none !important;
      border-radius: 12px !important;
      padding: ${3 * unit}px ${4 * unit}px !important;
      color: ${colors.white12} !important;
      font-size: 13px !important;
      height: 48px !important;
      box-sizing: border-box !important;
      
      &::placeholder {
        color: ${colors.grays.gray2} !important;
        font-size: 13px !important;
      }
      
      &:focus {
        outline: none !important;
      }
    }
    
    /* Style async-select component */
    & .react-select__control {
      background-color: transparent !important;
      border: 1px solid ${colors.grays.gray7} !important;
      border-radius: 12px !important;
      height: 40px !important;
      min-height: 40px !important;
      max-height: 40px !important;
      box-shadow: none !important;
      width: 100% !important;
      min-width: auto !important;
      max-width: 100% !important;
      padding: 0 !important;
      
      &:hover {
        border-color: ${colors.grays.gray7} !important;
      }
      
      &.react-select__control--is-focused {
        border-color: ${colors.greens.green6} !important;
        box-shadow: none !important;
      }
    }
    
    & .react-select__value-container {
      padding: 0 ${4 * unit}px !important;
      height: 40px !important;
      min-height: 40px !important;
      max-height: 40px !important;
      align-items: center !important;
      display: flex !important;
    }
    
    & .react-select__single-value {
      color: ${colors.white12} !important;
      font-size: 13px !important;
    }
    
    & .react-select__placeholder {
      color: ${colors.grays.gray2} !important;
      font-size: 14px !important;
    }
    
    & .react-select__input {
      color: ${colors.white12} !important;
      font-size: 13px !important;
    }
    
    & .react-select__menu {
      background-color: ${colors.grays.gray6} !important;
      border: 1px solid ${colors.grays.gray7} !important;
      border-radius: 12px !important;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3) !important;
      width: 100% !important;
      min-width: 100% !important;
    }
    
    & .react-select__menu-list {
      width: 100% !important;
      max-width: 100% !important;
      padding: 0 !important;
    }
    
    & .react-select__option {
      background-color: transparent !important;
      color: ${colors.white12} !important;
      font-size: 13px !important;
      padding: ${2 * unit}px ${4 * unit}px !important;
      
      &:hover {
        background-color: ${colors.grays.gray5} !important;
      }
      
      &.react-select__option--is-selected {
        background-color: ${colors.greens.green6} !important;
      }
    }
    
    & .react-select__dropdown-indicator {
      color: ${colors.grays.gray2} !important;
      padding: 0 ${2 * unit}px !important;
      
      svg {
        width: 16px !important;
        height: 16px !important;
      }
    }
    
    & .react-select__loading-indicator {
      color: ${colors.greens.green2} !important;
    }
    
    /* Hide the default label if present */
    & label {
      display: none !important;
    }
  }
  
  /* Ensure NewInput component containers don't interfere with AsyncSelect width */
  & > div > div {
    width: 100% !important;
    max-width: 100% !important;
    min-width: auto !important;
    display: block !important;
  }
`

export const selectWrapper = css`
  position: relative;
  width: 100%;
  max-width: 100%;
  border: 1px solid ${colors.grays.gray7};
  border-radius: 12px;
  
  &:focus-within {
    border-color: ${colors.greens.green6};
  }
`

export const selectInput = css`
  width: 100% !important;
  max-width: 100% !important;
  background-color: transparent !important;
  border: none !important;
  border-radius: 12px !important;
  padding: ${3 * unit}px ${4 * unit}px !important;
  padding-right: ${8 * unit}px !important; /* Extra space for arrow */
  color: ${colors.white12} !important;
  font-size: 13px !important;
  height: 48px !important;
  box-sizing: border-box !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  cursor: pointer !important;
  
  /* Force webkit browsers to use white color */
  -webkit-text-fill-color: ${colors.white12} !important;
  -webkit-text-stroke: 0px !important;
  
  /* Force color on focus */
  &:focus {
    outline: none !important;
    color: ${colors.white12} !important;
    -webkit-text-fill-color: ${colors.white12} !important;
  }
  
  /* Target empty value (placeholder state) */
  &[value=""] {
    color: ${colors.grays.gray2} !important;
    -webkit-text-fill-color: ${colors.grays.gray2} !important;
  }
  
  /* Target invalid state (when required and empty) */
  &:invalid {
    color: ${colors.grays.gray2} !important;
    -webkit-text-fill-color: ${colors.grays.gray2} !important;
  }
  
  /* Style option elements */
  & option {
    background-color: ${colors.grays.gray6} !important;
    color: ${colors.white12} !important;
    padding: ${2 * unit}px !important;
  }
  
  /* Style the placeholder/disabled option specifically */
  & option:disabled {
    color: ${colors.grays.gray2} !important;
    background-color: ${colors.grays.gray6} !important;
  }
  
  /* Target first option (placeholder) specifically */
  & option:first-child {
    color: ${colors.grays.gray2} !important;
  }
`

export const selectArrow = css`
  position: absolute;
  right: ${4 * unit}px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.grays.gray2};
  font-size: 13px;
  pointer-events: none;
  z-index: 10;
`

export const errorText = css`
  color: ${colors.errors.error3};
  font-size: 11px;
  margin-top: ${unit}px;
  display: block;
`

export const dropdownArrow = css`
  position: absolute;
  right: ${4 * unit}px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.grays.gray2};
  font-size: 13px;
  pointer-events: none;
  z-index: 10;
`

export const buttonContainer = css`
  margin-top: auto;
  padding: 0;
  width: 100%;
`

export const footerForm = css`
  width: 100%;
  max-width: 100%;
`

export const submitButton = css`
  width: 100%;
  background-color: ${colors.greens.green2};
  border: none;
  border-radius: 50px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${colors.greens.green3};
  }
  
  &:active {
    background-color: ${colors.greens.green4};
  }
`

export const buttonText = css`
  color: black;
  font-size: 13px;
  font-weight: ${font.weight.semiBold};
  margin: 0;
`

export const skipContainer = css`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: ${4 * unit}px;
  width: 100%;
  background: transparent;
`

export const skipText = css`
  color: ${colors.greens.green2};
  font-size: 13px;
  font-weight: ${font.weight.semiBold};
  cursor: pointer;
  margin: 0;
  background: transparent;
  
  &:hover {
    opacity: 0.8;
  }
`

export const progressContainer = css`
  padding: ${2 * unit}px ${4 * unit}px ${4 * unit}px;
  display: flex;
  justify-content: center;
  background: transparent;
`

export const progressBar = css`
  display: none;
  border-radius: 2px;
`

export const footerContainer = css`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${20 * unit}px;
  background-color: rgba(0, 0, 0, 0.3);
  border-top: none;
  backdrop-filter: blur(10px);
  box-shadow: 0 -8px 20px rgba(0, 0, 0, 0.4);
  z-index: 999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${2 * unit}px ${4 * unit}px;
  box-sizing: border-box;
`

export const footerText = css`
  color: ${colors.grays.gray2};
  font-size: 10px;
  margin: 0;
  opacity: 0.8;
` 